import { useState, useEffect } from "react";
import { useStudioErstellen } from "../../store/studioErstellenStore";

import classes from "./StudioSize.module.css";
import ErrorMessage from "./ErrorMessage";

export const SeventhStep = ({ validate }) => {
    const { studioData, updateStudioData } = useStudioErstellen();
    const [formData, setFormData] = useState({
        studioSize: studioData.studioSize || "",
        productionAreaSize: studioData.productionAreaSize || "",
        capacity: studioData.capacity || ""
    });

    // Keep local form state in sync with context (e.g. after clearStudioData)
    useEffect(() => {
        setFormData({
            studioSize: studioData.studioSize || "",
            productionAreaSize: studioData.productionAreaSize || "",
            capacity: studioData.capacity || ""
        });
    }, [studioData.studioSize, studioData.productionAreaSize, studioData.capacity]);

    const [errors, setErrors] = useState({
        studioSize: "",
        productionAreaSize: "",
        capacity: "",
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        // Only allow positive integers (remove any non-digit characters)
        const sanitizedValue = value.replace(/[^0-9]/g, '');
        
        setFormData(prevData => ({
            ...prevData,
            [name]: sanitizedValue
        }));
        updateStudioData(name, sanitizedValue);
    };

    const handleKeyDown = (e) => {
        // Prevent: decimal point, comma, minus, plus, 'e', 'E'
        if (['.', ',', '-', '+', 'e', 'E'].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleValidation = () => {
        let isValid = true;

        // Normalize values to strings to safely use .trim(),
        // even if the store holds numbers or other types.
        const studioSizeStr = formData.studioSize != null ? String(formData.studioSize).trim() : "";
        const productionAreaSizeStr = formData.productionAreaSize != null ? String(formData.productionAreaSize).trim() : "";
        const capacityStr = formData.capacity != null ? String(formData.capacity).trim() : "";

        const newErrors = {
            studioSize: "",
            capacity: "",
            productionAreaSize: "",
        };

        // Validate capacity
        if (capacityStr === "") {
            newErrors.capacity = "Personenanzahl muss ausgefüllt werden";
            isValid = false;
        } else {
            const capacityValue = Number(capacityStr);
            if (capacityValue < 1) {
                newErrors.capacity = "Mindestens 1 Person.";
                isValid = false;
            } else if (capacityValue > 1000) {
                newErrors.capacity = "Maximal 1000 Personen.";
                isValid = false;
            }
        }

        // Validate studioSize
        if (studioSizeStr === "") {
            newErrors.studioSize = "Größe muss ausgefüllt werden.";
            isValid = false;    
        } else {
            const studioSizeValue = Number(studioSizeStr);
            if (studioSizeValue < 1) {
                newErrors.studioSize = "Größe muss mindestens 1m² betragen.";
                isValid = false;
            } else if (studioSizeValue > 5000) {
                newErrors.studioSize = "Größe darf maximal 5000m² betragen.";
                isValid = false;
            }
        }

        // Validate productionAreaSize (same pattern as other required fields)
        if (productionAreaSizeStr === "") {
            newErrors.productionAreaSize = "Größe der Produktionsfläche muss ausgefüllt werden.";
            isValid = false;
        } else {
            const value = Number(productionAreaSizeStr);
            if (isNaN(value) || value < 1) {
                newErrors.productionAreaSize = "Größe der Produktionsfläche muss mindestens 1m² betragen.";
                isValid = false;
            } else if (value > 5000) {
                newErrors.productionAreaSize = "Größe der Produktionsfläche darf maximal 5000m² betragen.";
                isValid = false;
            }
        }

        // Production area must not be larger than total studio size
        const studioSizeTotal = Number(studioSizeStr);
        const productionAreaTotal = Number(productionAreaSizeStr);
        if (
            !isNaN(studioSizeTotal) &&
            !isNaN(productionAreaTotal) &&
            productionAreaTotal > studioSizeTotal &&
            !newErrors.productionAreaSize
        ) {
            newErrors.productionAreaSize = "Größe der Produktionsfläche darf nicht größer als die Gesamtfläche des Studios sein.";
            isValid = false;
        }

        setErrors(newErrors);
        return isValid;
    };

    useEffect(() => {
        validate.current = handleValidation;
    }, [validate, formData]);

  return (
    <div className={classes.mainContainer}>
        <div className={classes.textContainer}>
            <h2 className={`${classes.title} titleSemibold`}>Studio Größe & Kapazität</h2>
            <span  style={{color: 'var(--dark-grey-4)'}} className="slimRegularLargeText">Nenne die Größe deines Studios und wie viele Gäste du in deinem Studio haben möchtest.</span>
        </div>
        <div className={classes.inputContainer}>
            <div className={classes.inputWrapper} style={{width: window.innerWidth > 742 ? '' : '100%'}}>
                <h3 className={`${classes.inputTitle} semiboldMiddle `}>Wie groß ist dein Studio?</h3>
                <div className={classes.sizeContainer} style={{width: window.innerWidth > 742 ? '366px' : '100%'}}>
                    <input 
                        name="studioSize"
                        className="slimRegularLargeText" 
                        type="number" 
                        id="studioSize" 
                        placeholder="Größe des Studios" 
                        value={formData.studioSize}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        min="1"
                        step="1"
                        pattern="[0-9]*"
                    />
                    <div className={classes.inputDescriptionWrapper}>
                        <span className={`${classes.inputDescription} slimRegularLargeText`}>m²</span>
                    </div>
                </div>
                {errors.studioSize && <ErrorMessage>{errors.studioSize}</ErrorMessage>}
            </div>
            <div className={classes.inputWrapper} style={{width: window.innerWidth > 742 ? '' : '100%'}}>
                <h3 className={`${classes.inputTitle} semiboldMiddle `}>Wie groß ist die Produktionsfläche?</h3>
                <div className={classes.sizeContainer} style={{width: window.innerWidth > 742 ? '366px' : '100%'}}>
                    <input 
                        name="productionAreaSize"
                        className="slimRegularLargeText" 
                        type="number" 
                        id="productionAreaSize" 
                        placeholder="Größe der Produktionsfläche" 
                        value={formData.productionAreaSize}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        min="1"
                        step="1"
                        pattern="[0-9]*"
                    />
                    <div className={classes.inputDescriptionWrapper}>
                        <span className={`${classes.inputDescription} slimRegularLargeText`}>m²</span>
                    </div>
                </div>
                {errors.productionAreaSize && <ErrorMessage>{errors.productionAreaSize}</ErrorMessage>}
            </div>
            <div className={classes.peopleContainer}>
                <h3 className={`${classes.inputTitle} semiboldMiddle`}>Wie viele Personen passen in dein Studio?</h3>
                <input
                    className="slimRegularLargeText" 
                    name="capacity"
                    type="number"
                    id="studioCapacity"
                    placeholder="Personenanzahl"
                    value={formData.capacity}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    min="1"
                    step="1"
                    pattern="[0-9]*"
                />
                {errors.capacity && <ErrorMessage>{errors.capacity}</ErrorMessage>}
            </div>
        </div>
    </div>
  );
};

export default SeventhStep;